import React from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { sentenceCase } from 'utils/utilMethods';
import type { PaletteColor } from '@mui/material/styles/createPalette';

const paddingMap: Record<'small' | 'medium' | 'large', Record<string, string>> = {
  small: {
    text: '5px',
    outlined: '4px 10px',
    contained: '4px 10px',
  },
  medium: {
    text: '6px 8px',
    outlined: '6px 16px',
    contained: '6px 16px',
  },
  large: {
    text: '8px 11px',
    outlined: '8px 22px',
    contained: '8px 22px',
  },
};

const StyledButton = styled(Button)<ButtonProps>(({ theme, variant, color, size }) => {
  let borderColorValue = theme.palette.primary.main;
  if (
    variant === 'outlined'
    && color
    && ['primary', 'secondary', 'success', 'error', 'info', 'warning'].includes(color)
  ) {
    const paletteEntry = theme.palette[color as keyof typeof theme.palette];
    if (
      paletteEntry
      && typeof paletteEntry === 'object'
      && 'main' in paletteEntry
    ) {
      borderColorValue = (paletteEntry as PaletteColor).main;
    }
  }

  return {
    height: 38,
    borderRadius: 6,
    boxShadow: variant === 'contained' ? '0px 1px 1px 0px #00000040' : 'none',
    padding: paddingMap[size || 'medium'][variant || 'contained'],
    whiteSpace: 'nowrap',
    ...(variant === 'outlined' && {
      borderColor: borderColorValue,
    }),
    '& .MuiButton-startIcon': {
      marginRight: 8,
      marginLeft: -2,
      display: 'flex',
      alignItems: 'center',
    },
  };
});

export interface HLButtonProps extends ButtonProps {
  startIcon?: React.ReactNode;
}

function HLButton({ children, startIcon, ...props }: HLButtonProps) {
  const label = typeof children === 'string' ? sentenceCase(children) : children;

  return (
    <StyledButton
      disableElevation
      {...props}
      startIcon={startIcon}
    >
      {label}
    </StyledButton>
  );
}

export default HLButton;
